import {useState, useCallback} from 'react';
import {Download, Loader2} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {toast} from '@common-web/hooks/use-toast';
import {downloadCsvFile, makeCsvFilename} from '@common-web/lib/downloadCsv';

type RefetchFn = () => Promise<{data?: {csv?: string} | null | undefined}>;

export function ExportCsvButton({
  refetch,
  filenamePrefix,
}: {
  refetch: RefetchFn;
  filenamePrefix: string;
}) {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const result = await refetch();
      if (result.data?.csv) {
        downloadCsvFile(result.data.csv, makeCsvFilename(filenamePrefix));
      } else {
        toast({
          title: 'Export failed',
          description: 'No CSV data was returned. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Export failed',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  }, [refetch, filenamePrefix]);

  return (
    <Button
      variant='outline'
      onClick={handleExport}
      disabled={isExporting}
      className='border-gray-200 hover:border-brand-blue transition-colors'
    >
      {isExporting ? (
        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
      ) : (
        <Download className='h-4 w-4 mr-2' />
      )}
      Export CSV
    </Button>
  );
}
